import './AggregatedLock.scss';
import { Link } from 'react-router-dom';
import type { AggregatedInfo } from './useAggregatedInfo';

const n = (v: number) => Number(v || 0).toLocaleString('ko-KR');

/**
 * 가져온 공고의 상단 배너 (aggregated-posting-lock-design.md 3.2 · 5.2)
 * - 사역자·비선점자에게: [가져온 공고] 배지 + 성과(조회·관심 표시) + 잠금 안내 + 인수하기
 * - 인수한 교회에게: [선점 완료] + 성과 + 지원자 관리 바로가기
 */
export default function AggregatedLockBanner({ info, applyKind }: { info: AggregatedInfo; applyKind: boolean }) {
  if (!info.managed) return null;
  const claimPath = `/recruit/claim/${info.kind}/${info.id}`;

  if (info.unpublished === 'EXPIRED') {
    return (
      <div className="agg-banner agg-banner--muted">
        <div className="agg-banner__head">
          <span className="agg-badge">가져온 공고</span>
          <strong>교회가 확인하지 않아 30일이 지나 내려간 공고입니다.</strong>
        </div>
        <p className="agg-banner__desc">지원은 원문 게시글의 안내를 확인해 주세요. 이 공고의 담당자이시면 인수해서 다시 게시할 수 있습니다.</p>
        <div className="agg-banner__actions">
          {info.sourceUrl && <a className="agg-btn" href={info.sourceUrl} target="_blank" rel="noopener noreferrer">원문 보기</a>}
          {info.claimable && <Link className="agg-btn agg-btn--primary" to={claimPath}>인수하고 다시 게시하기</Link>}
        </div>
      </div>
    );
  }

  if (info.claimStatus === 'CLAIMED') {
    return (
      <div className="agg-banner agg-banner--claimed">
        <div className="agg-banner__head">
          <span className="agg-badge agg-badge--claimed">{info.isOwner ? '관리 중' : '교회 확인'}</span>
          <strong>{info.isOwner ? '인수한 공고입니다' : `${info.church || '교회'}가 사역자모아에서 직접 관리하는 공고입니다.`}</strong>
        </div>
        <div className="agg-stats">
          <span>조회 <b>{n(info.viewCount)}</b></span>
          {applyKind && <span>관심 표시 <b>{n(info.interestCount)}</b></span>}
        </div>
        {info.isOwner && applyKind && (
          <div className="agg-banner__actions">
            <Link className="agg-btn agg-btn--primary" to={`/mypage/applicantmanage?id=${info.id}&tableType=${info.kind}`}>지원자 관리</Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="agg-banner">
      <div className="agg-banner__head">
        <span className="agg-badge">가져온 공고</span>
        <span className="agg-banner__source">{info.sourceBoard ? `${info.sourceBoard} 게시판에서 옮겨 온 공고` : '게시판에서 옮겨 온 공고'}</span>
      </div>
      <div className="agg-stats">
        <span>조회 <b>{n(info.viewCount)}</b></span>
        {applyKind && <span>관심 표시 <b>{n(info.interestCount)}</b></span>}
      </div>
      <div className="agg-lock">
        <div className="agg-lock__text">
          <strong>🔒 지원자 목록과 관리는 교회가 인수한 뒤에 열립니다.</strong>
          {info.claimStatus === 'CLAIM_PENDING' && <span>교회 담당자 인증을 기다리고 있습니다.</span>}
        </div>
        {info.claimable && (
          <Link className="agg-btn agg-btn--primary" to={claimPath}>이 공고의 교회인가요? 인수하기</Link>
        )}
      </div>
    </div>
  );
}

/** 공고 하단 고정 문구 (10.1 — 출처 명시 · 재작성 고지 · 삭제 요청) */
export function AggregatedSourceNotice({ info }: { info: AggregatedInfo }) {
  if (!info.managed) return null;
  return (
    <div className="agg-source">
      <p>
        출처: {info.sourceBoard || '원문 게시판'}
        {info.sourceUrl && (
          <> · <a href={info.sourceUrl} target="_blank" rel="noopener noreferrer">원문 보기 ›</a></>
        )}
      </p>
      <p>본 공고는 원문을 요약해 사역자모아 양식으로 작성했습니다.</p>
      {info.claimStatus !== 'CLAIMED' && (
        <p>
          담당자이시며 수정·삭제를 원하시면 <Link to={`/recruit/claim/${info.kind}/${info.id}?mode=delete`}>[삭제 요청]</Link>을 눌러주세요.
        </p>
      )}
    </div>
  );
}
